"use client";

/**
 * Pricing Section Component
 *
 * Full pricing section with toggle and cards.
 * Uses shared plans-data.ts for single source of truth.
 * Icons are rendered directly in PricingCard for proper animation control.
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, useContext, useMemo, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { T } from "gt-next";
import { SetupLayoutContext } from "@/app/app/(user-pages)/setup/SetupLayoutContext";
import { TouchTooltip } from "@/components/ui/tooltip";
import PricingToggle from "./PricingToggle";
import PricingCards from "./PricingCards";
import ComparePlans from "./ComparePlans";
import { type PricingPlan } from "./PricingCard";
import { PRICING_PLANS } from "./plans-data";
import type { PlanType } from "./comparisonData";

// Re-export for backwards compatibility
export { PRICING_PLANS };

/**
 * Isolated seats counter — only this component re-renders on count change.
 * Parent Pricing and all cards/comparison table are unaffected.
 * Uses chained setTimeout (not setInterval) for natural, non-uniform timing.
 */
const SeatsIndicator = memo(function SeatsIndicator() {
  const $ = _c(15);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const d = new Date();
    t0 = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100;
    t1 = d.getDate();
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  const seed = t0 + t1;
  const dailySeats = seed % 4 + 2;
  const [seatCount] = useState(dailySeats);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const timerRef = useRef(null);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const t = setTimeout(() => setIsLoading(false), 1200);
      return () => clearTimeout(t);
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== isLoading) {
    t4 = () => {
      if (isLoading) {
        return;
      }
      const scheduleNext = () => {
        const delay = 20000 + Math.random() * 15000;
        timerRef.current = setTimeout(() => {
          setRefreshKey(_temp);
          scheduleNext();
        }, delay);
      };
      scheduleNext();
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    };
    t5 = [isLoading];
    $[4] = isLoading;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div><p className="text-[13px] font-semibold text-gray-200">Limited monthly capacity</p><p className="mt-1.5 text-[12px] leading-[1.5] text-gray-500">We only accept a limited number of new accounts each month to ensure dedicated attention and real results for every creator. Once spots fill, registration closes until next month.</p></div>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isLoading || $[9] !== refreshKey || $[10] !== seatCount) {
    t7 = <div className="flex h-[22px] items-center"><AnimatePresence mode="wait" initial={false}>{isLoading ? <motion.div key="skeleton" className="flex h-[22px] items-center" exit={{
          opacity: 0,
          filter: "blur(6px)",
          transition: {
            duration: 0.15,
            ease: "easeIn"
          }
        }}><motion.span className="block h-[22px] w-[48px] rounded-[5px] bg-black/[0.12]" animate={{
            opacity: [0.45, 0.75, 0.45]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: "easeInOut"
          }} /></motion.div> : <motion.div key="loaded" className="flex h-[22px] items-center gap-2" initial={{
          opacity: 0,
          filter: "blur(6px)"
        }} animate={{
          opacity: 1,
          filter: "blur(0px)"
        }} whileHover="hover" transition={{
          duration: 0.35,
          ease: [0.25, 0.1, 0.25, 1]
        }}><motion.svg className="h-[22px] w-[22px] shrink-0" viewBox="0 0 24 24" fill="none" initial={{
            scale: 0.5,
            filter: "drop-shadow(0 0 0px rgba(234, 88, 12, 0))"
          }} animate={{
            scale: 1,
            filter: "drop-shadow(0 0 3px rgba(234, 88, 12, 0.35))"
          }} variants={{
            hover: {
              scale: 1.15,
              filter: "drop-shadow(0 0 6px rgba(234, 88, 12, 0.55))",
              transition: {
                type: "spring",
                stiffness: 400,
                damping: 15
              }
            }
          }} transition={{
            delay: 0.1,
            type: "spring",
            damping: 12,
            stiffness: 200
          }}><path d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.047 8.287 8.287 0 009 9.601a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" fill="#ea580c" /><path d="M12 18a3.75 3.75 0 00.495-7.468 5.99 5.99 0 00-1.925 3.547 5.975 5.975 0 01-2.133-1.001A3.75 3.75 0 0012 18z" fill="#fdba74" /></motion.svg><AnimatePresence mode="wait"><motion.span key={`${seatCount}-${refreshKey}`} className="text-[16px] font-bold tabular-nums leading-none text-black/80" initial={{
              opacity: 0,
              filter: "blur(4px)"
            }} animate={{
              opacity: 1,
              filter: "blur(0px)"
            }} exit={{
              opacity: 0,
              filter: "blur(4px)"
            }} transition={{
              duration: 0.15,
              ease: "easeOut"
            }}>{seatCount}</motion.span></AnimatePresence></motion.div>}</AnimatePresence></div>;
    $[8] = isLoading;
    $[9] = refreshKey;
    $[10] = seatCount;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="text-[16px] font-medium leading-none text-black/45"><T>seats left</T></span>;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7) {
    t9 = <TouchTooltip content={t6} side="bottom" variant="dark" contentClassName="max-w-[260px] rounded-xl px-4 py-3"><div className="inline-flex cursor-default items-center gap-2">{t7}{t8}</div></TouchTooltip>;
    $[13] = t7;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
});
interface PricingProps {
  isSetupProcess?: boolean;
  selectedPeriod?: "Monthly" | "Yearly";
  showHeader?: boolean;
  className?: string;
}
export default function Pricing(t0) {
  const $ = _c(28);
  const {
    isSetupProcess: t1,
    selectedPeriod: t2,
    showHeader: t3,
    className: t4
  } = t0;
  const isSetupProcess = t1 === undefined ? false : t1;
  const selectedPeriod = t2 === undefined ? "Monthly" : t2;
  const showHeader = t3 === undefined ? true : t3;
  const className = t4 === undefined ? "" : t4;
  const [activePeriod, setActivePeriod] = useState(selectedPeriod);
  const [focusedPlan, setFocusedPlan] = useState("Grow");
  const setupContext = useContext(SetupLayoutContext);
  let t5;
  if ($[0] !== isSetupProcess || $[1] !== setupContext) {
    t5 = period => {
      setActivePeriod(period);
      if (isSetupProcess && setupContext?.setActivePeriod) {
        setupContext.setActivePeriod(period);
      }
    };
    $[0] = isSetupProcess;
    $[1] = setupContext;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const handlePeriodChange = t5;
  let t6;
  if ($[3] !== isSetupProcess || $[4] !== setupContext) {
    t6 = plan => {
      if (isSetupProcess && setupContext?.setSelectedPlan) {
        setupContext.setSelectedPlan(plan as any);
      }
    };
    $[3] = isSetupProcess;
    $[4] = setupContext;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const handlePlanSelected = t6;
  let t7;
  bb0: {
    if (!isSetupProcess) {
      t7 = undefined;
      break bb0;
    }
    let t8;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = PRICING_PLANS.map(_temp2);
      $[6] = t8;
    } else {
      t8 = $[6];
    }
    t7 = t8;
  }
  const customPlans = t7;
  const t8 = `px-3 pb-4 pt-[120px] md:pb-8 md:pt-[140px] lg:pb-12 lg:pt-[160px] ${className}`;
  let t9;
  if ($[7] !== showHeader) {
    t9 = showHeader && <div className="mb-10 flex w-full flex-col items-center gap-4 text-center"><h1 className="font-mosans uppercase leading-[0.9] tracking-[-0.01em] text-black" style={{
        fontSize: "clamp(40px, 7vw, 80px)"
      }}><T>Grow or money-back.</T></h1><SeatsIndicator /><p className="text-body-md text-black/70 md:text-body-lg" style={{
        maxWidth: "620px"
      }}><T>100% real followers or full refund—no questions asked.<br /><span className="text-black/50">Plans from $2/day. Cancel anytime.</span></T></p></div>;
    $[7] = showHeader;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== activePeriod || $[10] !== handlePeriodChange) {
    t10 = <div className="mb-6 md:mb-10"><PricingToggle activePeriod={activePeriod} onToggle={handlePeriodChange} /></div>;
    $[9] = activePeriod;
    $[10] = handlePeriodChange;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const t11 = isSetupProcess ? handlePlanSelected : undefined;
  const t12 = !isSetupProcess;
  let t13;
  if ($[12] !== activePeriod || $[13] !== customPlans || $[14] !== t11 || $[15] !== t12) {
    t13 = <div><PricingCards activePeriod={activePeriod} size="default" plans={customPlans} onPlanSelected={t11} hideRenewalPrice={t12} /></div>;
    $[12] = activePeriod;
    $[13] = customPlans;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== activePeriod || $[18] !== focusedPlan) {
    t14 = <div className="mt-2 md:mt-12"><ComparePlans activePlan={focusedPlan} onPlanChange={setFocusedPlan} activePeriod={activePeriod} /></div>;
    $[17] = activePeriod;
    $[18] = focusedPlan;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t10 || $[21] !== t13 || $[22] !== t14 || $[23] !== t9) {
    t15 = <div className="mx-auto max-w-container">{t9}{t10}{t13}{t14}</div>;
    $[20] = t10;
    $[21] = t13;
    $[22] = t14;
    $[23] = t9;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t15 || $[26] !== t8) {
    t16 = <section className={t8}>{t15}</section>;
    $[25] = t15;
    $[26] = t8;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  return t16;
}
function _temp2(plan_0) {
  return {
    ...plan_0,
    button: {
      ...plan_0.button,
      label: "Start Your Growth"
    }
  };
}
function _temp(k) {
  return k + 1;
}